import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Button } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputNumberModule } from 'primeng/inputnumber';
import { ListboxModule } from 'primeng/listbox';
import { RadioButton } from 'primeng/radiobutton';
import { SelectModule } from 'primeng/select';
import { dateToIsoString, parseLocalDate } from '../../common/functions';
import { IRecurrenceConfiguration, IRecurrenceSchedule } from '../../services/insecta/insecta.models';

@Component({
  selector: 'app-recurrence-schedule',
  standalone: true,
  imports: [DatePickerModule, FormsModule, CommonModule, Button, RadioButton, FloatLabelModule, SelectModule, InputNumberModule, ListboxModule],
  templateUrl: './recurrence-schedule.component.html'
})
export class RecurrenceScheduleComponent implements OnChanges {
  @Input() orderItem: any;
  @Input() minDate!: Date;
  @Input() maxDate!: Date;
  @Input() invalidDays: number[] = [];
  @Input() invalidDates: Date[] = [];
  @Input() invalidDatesStr: string[] = [];
  @Input() firstDeliveryDate: Date | null = null;
  @Input() invalidRecurrenceDates: string[] = [];
  @Input() recurrenceSchedule: IRecurrenceSchedule | undefined = undefined;

  @Output() updateRecurrences = new EventEmitter<any>();

  recurrenceDates: string[] = [];
  buildMyOwnSchedule: boolean = true;
  repeatInterval: number = 1;
  repeatUnit: string = 'week';
  repeatUnitOptions = [
    { label: 'Week', value: 'week' },
    { label: 'Month', value: 'month' }
  ];

  weekDays = [
    { label: 'M', value: 1, fullName: 'Monday' },
    { label: 'T', value: 2, fullName: 'Tuesday' },
    { label: 'W', value: 3, fullName: 'Wednesday' },
    { label: 'T', value: 4, fullName: 'Thursday' },
    { label: 'F', value: 5, fullName: 'Friday' },
    { label: 'S', value: 6, fullName: 'Saturday' },
    { label: 'S', value: 0, fullName: 'Sunday' }
  ];
  selectedWeekDays: number[] = [];
  stopDate: Date | null = null;
  lastDeliveryDateDisabled: boolean = true;

  ngOnChanges() {
    if (this.firstDeliveryDate) {
      this.lastDeliveryDateDisabled = false;
    }

    if (this.recurrenceSchedule) {
      this.buildMyOwnSchedule = this.recurrenceSchedule.scheduleType !== 'recurring';
      this.repeatInterval = this.recurrenceSchedule.configuration!.repeatEvery!;
      this.repeatUnit = this.recurrenceSchedule.configuration!.repeatUnit!;
      this.selectedWeekDays = (this.recurrenceSchedule.configuration!.daysOfWeek || [])
        .map((day) => this.weekDays.find((d) => d.fullName === day)?.value || 0)
        .filter((v) => v !== 0);
      this.stopDate = parseLocalDate(this.recurrenceSchedule.configuration!.endDate!);
      this.recurrenceDates = this.recurrenceSchedule.configuration!.customDates || [];
    }
  }

  toggleWeekDay(day: number) {
    if (this.selectedWeekDays.includes(day)) {
      this.selectedWeekDays = this.selectedWeekDays.filter((d) => d !== day);
    } else {
      this.selectedWeekDays = [...this.selectedWeekDays, day];
    }
  }

  buildSchedule() {
    if (!this.firstDeliveryDate || this.selectedWeekDays.length === 0) return;

    const validDatesSet = new Set<string>();
    this.invalidRecurrenceDates = [];

    validDatesSet.add(dateToIsoString(this.firstDeliveryDate));
    const weekStart = new Date(this.firstDeliveryDate);
    weekStart.setDate(weekStart.getDate() - weekStart.getDay());
    const effectiveMaxDate = this.stopDate ? this.stopDate : this.maxDate;

    while (weekStart <= effectiveMaxDate) {
      for (const weekday of this.selectedWeekDays) {
        const date = new Date(weekStart);
        date.setDate(weekStart.getDate() + ((weekday - weekStart.getDay() + 7) % 7));

        if (date < this.firstDeliveryDate || date > effectiveMaxDate) continue;

        const dateStr = dateToIsoString(date);

        if (this.invalidDays.includes(date.getDay()) || this.invalidDatesStr.some((dStr) => dStr === dateStr)) {
          this.invalidRecurrenceDates.push(dateStr);
        } else {
          validDatesSet.add(dateStr);
        }
      }
      weekStart.setDate(weekStart.getDate() + 7 * this.repeatInterval);
    }

    this.recurrenceDates = Array.from(validDatesSet);

    const recurrenceConfiguration: IRecurrenceConfiguration = {
      repeatEvery: this.repeatInterval,
      repeatUnit: this.repeatUnit,
      daysOfWeek: this.selectedWeekDays.map((day) => this.weekDays.find((d) => d.value === day)?.fullName || ''),
      endDate: this.stopDate === null ? '' : dateToIsoString(this.stopDate),
      customDates: this.recurrenceDates
    };

    const recurrenceSchedule: IRecurrenceSchedule = {
      firstDeliveryDate: this.firstDeliveryDate === null ? '' : dateToIsoString(this.firstDeliveryDate),
      scheduleType: this.buildMyOwnSchedule ? 'own' : 'recurring',
      configuration: recurrenceConfiguration
    };

    this.updateRecurrences.emit({
      recurrenceDates: this.recurrenceDates,
      invalidRecurrenceDates: this.invalidRecurrenceDates,
      recurrenceSchedule: recurrenceSchedule
    });
  }

  onBuildMyOwnSelected() {
    if (this.buildMyOwnSchedule) {
      this.reset();
    } else {
      if (!this.firstDeliveryDate) {
        this.stopDate = null;
      }
    }
  }

  save() {
    throw new Error('Method not implemented.');
  }

  firstDeliveryDateChanged() {
    this.lastDeliveryDateDisabled = this.firstDeliveryDate ? false : true;
  }

  canBuildSchedule(): boolean {
    if (this.firstDeliveryDate && this.stopDate && this.repeatInterval && this.repeatUnit && this.selectedWeekDays.length > 0) {
      return true;
    } else {
      return false;
    }
  }

  removeInvalid() {
    this.invalidRecurrenceDates = [];
    this.updateRecurrences.emit({
      recurrenceDates: this.recurrenceDates,
      invalidRecurrenceDates: []
    });
  }

  public reset() {
    this.firstDeliveryDate = null;
    this.invalidRecurrenceDates = [];
    this.recurrenceDates = [];
    this.buildMyOwnSchedule = true;
    this.repeatInterval = 1;
    this.repeatUnit = 'week';
    this.selectedWeekDays = [];
    this.stopDate = null;
    this.lastDeliveryDateDisabled = true;
    this.recurrenceSchedule = undefined;

    this.updateRecurrences.emit({
      recurrenceDates: [],
      invalidRecurrenceDates: []
    });
  }
}
